import { EventBus } from './modules/EventBus.js';
import { Storage } from './modules/Storage.js';
import { ConsentAPI } from './modules/ConsentAPI.js';
import { PolicyVersion } from './modules/PolicyVersion.js';
import { ScriptLoader } from './modules/ScriptLoader.js';
import { Banner } from './modules/Banner.js';
import { Modal } from './modules/Modal.js';
import { GoogleConsentMode } from './modules/integrations/GoogleConsentMode.js';
import { MetaPixel } from './modules/integrations/MetaPixel.js';
import { ClarityUet } from './modules/integrations/ClarityUet.js';
import { Matomo } from './modules/integrations/Matomo.js';
import { Plausible } from './modules/integrations/Plausible.js';
import { DataLayer } from './modules/integrations/DataLayer.js';
import { ShadowMode } from './modules/ShadowMode.js';
import { ReopenButton } from './modules/ReopenButton.js';

export const VERSION = '1.4.2';

const TEXTS_FALLBACK = {
  title: 'Cookies', body: 'Usamos cookies…',
  accept: 'Aceptar todo', reject: 'Rechazar todo', configure: 'Configurar',
  save: 'Guardar', revoke: 'Revocar todo',
  purposes: { functional: 'Funcional', measurement: 'Medición', marketing: 'Marketing', personalization: 'Personalización' },
  policyLink: 'Política de cookies',
  reopen: 'Preferencias de cookies',
};

function mergeTexts(custom = {}) {
  return {
    ...TEXTS_FALLBACK,
    ...custom,
    purposes: { ...TEXTS_FALLBACK.purposes, ...(custom.purposes ?? {}) },
  };
}

export class NedawareCmp {
  constructor(config) {
    this.config = config;
    this.texts = mergeTexts(config.texts);
    this.bus = new EventBus();
    this.storage = new Storage({ cookieName: 'nedaware_cmp', cookieDomain: config.cookieDomainRoot || '' });
    this.api = new ConsentAPI({ config, bus: this.bus, storage: this.storage });
    this.scriptLoader = new ScriptLoader();
    this._readyResolver = null;
    this.ready = new Promise((r) => { this._readyResolver = r; });

    this._wireIntegrations();
    this._init();
  }

  _wireIntegrations() {
    this.bus.on('change', (state) => {
      GoogleConsentMode.sync(state);
      MetaPixel.sync(state);
      ClarityUet.sync(state);
      Matomo.sync(state);
      Plausible.sync(state, this.config.plausible || {});
      DataLayer.sync(state);
      const granted = new Set(Object.entries(state).filter(([, v]) => v).map(([k]) => k));
      this.scriptLoader.promote(granted);
      this._postRecord(state);
    });
    // Audit trail for explicit revocations: post a record with the uuid
    // that the user previously consented under, so the AEPD/auditor can
    // demonstrate when consent was withdrawn.
    this.bus.on('revoke', (revokedUuid) => {
      this._postRevokeRecord(revokedUuid);
    });
  }

  _init() {
    if (this.config.shadowMode) {
      const sm = new ShadowMode({
        mapping: this.config.shadowMapping || { c51: 'functional', c52: 'marketing', c54: 'measurement' },
        postRecord: ({ purposes, source, legacyRaw }) => {
          this._send({
            uuid: crypto.randomUUID?.() ?? Date.now().toString(36),
            purposes,
            policy_version: this.config.policyVersion,
            language: this.config.language,
            source,
          });
        },
      });
      sm.start();
      this._readyResolver(this);
      return;
    }

    const stored = this.storage.read();
    const needsPrompt = !stored || PolicyVersion.needsReprompt(stored.v, this.config.policyVersion);
    if (!needsPrompt) {
      this.bus.emit('change', this.api.all());
    } else {
      this._showBanner();
    }

    if (this.config.reopenButtonEnabled !== false) {
      this._reopenBtn = new ReopenButton({
        label: this.texts.reopen,
        position: this.config.reopenButtonPosition ?? 'bottom-left',
        onClick: () => this.openModal(),
      });
      // Show only once banner is gone (no banner now or after first decision)
      if (!needsPrompt) {
        this._reopenBtn.show();
      } else {
        this.bus.on('change', () => this._reopenBtn.show());
      }
    }

    this._readyResolver(this);
  }

  _showBanner() {
    const banner = new Banner({
      texts: this.texts,
      theme: this.config.theme,
      firstVisit: !this.storage.read(),
      onAccept: () => this._setAll(true),
      onReject: () => this._setAll(false),
      onConfigure: () => this.openModal(),
    });
    banner.show();
  }

  _setAll(granted) {
    const next = {};
    for (const p of this.config.activePurposes) next[p] = granted || p === 'functional';
    this.api.set(next);
  }

  openModal() {
    new Modal({
      texts: this.texts,
      vendors: this.config.vendors || [],
      activePurposes: this.config.activePurposes,
      initialState: this.api.all(),
      policyPageUrl: this.config.policyPageUrl,
      onSave: (state) => this.api.set(state),
      onRevoke: () => this.api.revoke(),
    }).show();
  }

  get(p) { return this.api.get(p); }
  all() { return this.api.all(); }
  on(e, cb) { return this.bus.on(e, cb); }
  open() { this.openModal(); }
  revoke() { this.api.revoke(); }
  get version() { return VERSION; }
  get policyVersion() { return this.config.policyVersion; }

  /**
   * Where a consent record goes:
   * - WordPress page (restUrl + nonce): POST /record with the nonce, as always.
   * - Front-end outside WordPress (no nonce) with `recordUrl`: POST to that
   *   absolute URL (/record-remote) as a CORS "simple request" — text/plain,
   *   no-cors, no cookies — so it needs no preflight; the server checks the
   *   Origin against its allowlist. The response is opaque and not needed.
   * - Neither: nothing is sent.
   */
  _recordTransport() {
    const { restUrl, nonce, recordUrl } = this.config;
    if (restUrl && nonce) {
      return {
        url: `${restUrl}record`,
        init: { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce } },
      };
    }
    if (recordUrl) {
      return {
        url: recordUrl,
        init: {
          method: 'POST', mode: 'no-cors', credentials: 'omit', keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        },
      };
    }
    return null;
  }

  _send(body) {
    const t = this._recordTransport();
    if (!t) return;
    try {
      fetch(t.url, { ...t.init, body: JSON.stringify(body) }).catch(() => {});
    } catch (_) { /* never break the page for an audit record */ }
  }

  _postRecord(state) {
    if (!this._recordTransport()) return;
    // Guard: tras revoke() el uuid queda en null y bus.emit('change')
    // dispararía un POST con uuid=null que el endpoint rechaza con 400.
    // El log de la revocación ya viaja por _postRevokeRecord (source=api_revoke).
    const uuid = this.api.uuid();
    if (!uuid) return;
    const purposes = Object.entries(state).filter(([, v]) => v).map(([k]) => k);
    this._send({
      uuid,
      purposes,
      policy_version: this.config.policyVersion,
      language: this.config.language,
      source: this.config.shadowMode ? 'shadow' : 'banner',
    });
  }

  _postRevokeRecord(revokedUuid) {
    if (!revokedUuid) return;
    this._send({
      uuid: revokedUuid,
      purposes: ['functional'],
      policy_version: this.config.policyVersion,
      language: this.config.language,
      source: 'api_revoke',
    });
  }
}

export function bootstrap() {
  if (typeof window === 'undefined') return;
  const cfg = window.NedawareCmpConfig;
  if (!cfg) {
    // No config: install a minimal stub so smoke tests and no-config pages still
    // have a predictable window.NedawareCmp with a .version property.
    window.NedawareCmp = window.NedawareCmp ?? { version: VERSION };
    return;
  }
  const inst = new NedawareCmp(cfg);
  window.NedawareCmp = inst;
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-nedaware-cmp="open"]');
    if (trigger) { e.preventDefault(); inst.open(); }
  });
}

bootstrap();
